const PRODUCT_PAGE_SIZE = 1000;
const LEGACY_STOCK_BACKFILL_PREFIX = 'legacy-stock-backfill';

const safeStorageGet = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures in private mode or restricted environments.
  }
};

export const fetchAllPaginated = async (buildQuery, pageSize = PRODUCT_PAGE_SIZE) => {
  const rows = [];
  let from = 0;

  while (true) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;

    const chunk = Array.isArray(data) ? data : [];
    rows.push(...chunk);

    if (chunk.length < pageSize) break;
    from += pageSize;
  }

  return rows;
};

export const getLegacyVariantStock = (product) => {
  const variants = Array.isArray(product?.product_variants) ? product.product_variants : [];
  return variants.reduce((acc, variant) => acc + Number(variant?.stock_quantity || 0), 0);
};

export const getProductStockForSite = (product, siteId = null) => {
  const levels = Array.isArray(product?.stock_levels) ? product.stock_levels : [];

  if (levels.length > 0) {
    if (siteId) {
      return levels.reduce((acc, level) => {
        if (String(level?.site_id || '') !== String(siteId)) return acc;
        return acc + Number(level?.quantity || 0);
      }, 0);
    }
    return levels.reduce((acc, level) => acc + Number(level.quantity || 0), 0);
  }

  return getLegacyVariantStock(product);
};

export const resolvePreferredStockSiteId = (organization, sites = []) => {
  if (organization?.default_site_id) return organization.default_site_id;

  const safeSites = Array.isArray(sites) ? sites : [];
  // ✅ FIX bug #8 multi-sucursal: si la organización tiene una sola sucursal, el
  // fallback a esa sucursal es seguro. Si tiene varias y NO definió un default
  // explícito, devolvemos null para evitar que la migración legacy de stock vuelque
  // todo a una sucursal arbitraria. El caller debe manejar null y abortar la
  // migración pidiendo al usuario que elija default_site_id en Configuración.
  if (safeSites.length === 1) {
    const onlySite = safeSites[0];
    if (onlySite?.is_active !== false) return onlySite?.id || null;
    return null;
  }
  return null;
};

export const hydrateLegacyVariantStock = async ({
  supabase,
  organizationId,
  defaultSiteId,
  force = false,
  pageSize = PRODUCT_PAGE_SIZE,
}) => {
  if (!supabase || !organizationId || !defaultSiteId) {
    return { inserted: 0, skipped: true, reason: 'missing-context' };
  }

  const storageKey = `${LEGACY_STOCK_BACKFILL_PREFIX}:${organizationId}:${defaultSiteId}`;
  if (!force && safeStorageGet(storageKey) === 'done') {
    return { inserted: 0, skipped: true, reason: 'already-done' };
  }

  const products = await fetchAllPaginated(
    () =>
      supabase
        .from('products')
        .select('id, stock_levels(id, site_id), product_variants(stock_quantity)')
        .eq('organization_id', organizationId)
        .order('id', { ascending: true }),
    pageSize,
  );

  const pendingRows = products
    .filter((product) => {
      const levels = Array.isArray(product?.stock_levels) ? product.stock_levels : [];
      return levels.length === 0 && getLegacyVariantStock(product) > 0;
    })
    .map((product) => ({
      organization_id: organizationId,
      product_id: product.id,
      site_id: defaultSiteId,
      quantity: getLegacyVariantStock(product),
      min_stock: 0,
    }));

  if (pendingRows.length === 0) {
    safeStorageSet(storageKey, 'done');
    return { inserted: 0, skipped: false, reason: 'nothing-to-migrate' };
  }

  for (let index = 0; index < pendingRows.length; index += 200) {
    const batch = pendingRows.slice(index, index + 200);
    const { error } = await supabase.from('stock_levels').upsert(batch, { onConflict: 'product_id,site_id' });
    if (error) throw error;
  }

  safeStorageSet(storageKey, 'done');
  return { inserted: pendingRows.length, skipped: false };
};
